import { Callout, FormField, Input } from '@dev-spendesk/grapes';

import { useTranslation } from 'common/hooks/useTranslation';

export const VAT_NUMBER_MIN_LENGTH = 7;
export const VAT_NUMBER_MAX_LENGTH = 25;

type Props = {
  vatNumber?: string;
  error?: string | false;
  isDisabled?: boolean;
  showLabel?: boolean;
  showHelperText?: boolean;
  className?: string;
  onChange(vatNumber?: string): void;
};

export const VatNumberFormField = ({
  vatNumber,
  error,
  isDisabled = false,
  showLabel = true,
  showHelperText = false,
  className = 'mt-16',
  onChange,
}: Props) => {
  const { t } = useTranslation('global');

  const getErrorMessage = (
    errorType: string | undefined,
  ): string | undefined => {
    if (!errorType) {
      return undefined;
    }
    if (errorType === 'required') {
      return t('misc.requiredField');
    }
    if (errorType === 'invalidLength') {
      return t(
        'suppliers.spanishDprCompliance.missingSupplierDetailsCallout.vatNumber.invalidLength',
        { min: VAT_NUMBER_MIN_LENGTH, max: VAT_NUMBER_MAX_LENGTH },
      );
    }
    return errorType;
  };

  const errorMessage = getErrorMessage(error || undefined);

  const input = (
    <Input
      name="vatNumber"
      value={vatNumber}
      placeholder={t(
        'suppliers.spanishDprCompliance.missingSupplierDetailsCallout.vatNumber.placeholder',
      )}
      onChange={(event) => {
        if (event.target.checkValidity()) {
          onChange(event.target.value.trim());
        }
      }}
      isInvalid={Boolean(error)}
      isDisabled={isDisabled}
      fit="parent"
    />
  );

  return showLabel ? (
    <FormField
      className={className}
      alertMessage={errorMessage}
      label={t(
        'suppliers.spanishDprCompliance.missingSupplierDetailsCallout.vatNumber.label',
      )}
      infoTipContent={t(
        'suppliers.spanishDprCompliance.missingSupplierDetailsCallout.vatNumber.hint',
      )}
    >
      {showHelperText && (
        <p className="text-content-secondary-bg-primary body-s mb-8">
          {t(
            'suppliers.spanishDprCompliance.missingSupplierDetailsCallout.vatNumber.lengthHint',
            { min: VAT_NUMBER_MIN_LENGTH, max: VAT_NUMBER_MAX_LENGTH },
          )}
        </p>
      )}
      {input}
    </FormField>
  ) : (
    <div className={className}>
      {input}
      {error && (
        <Callout
          variant="alert"
          className="mt-8"
          title={errorMessage ?? t('misc.requiredField')}
        />
      )}
    </div>
  );
};
